import { useMemo } from "react";
import { Mail, Phone, Instagram, Facebook, Globe, X as XIcon, Youtube, Music2, Link as LinkIcon, Trophy, Video, Trash2, Plus, Lock, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhoneInput from "@/components/PhoneInput";
import { SOCIAL_TYPES, extractSocialHandle } from "@/lib/social";

// Order + labels for the contact type dropdown.
export const CONTACT_TYPES = [
    ["phone", "Phone"],
    ["email", "Email"],
    ["instagram", "Instagram"],
    ["tiktok", "TikTok"],
    ["youtube", "YouTube"],
    ["x", "X (Twitter)"],
    ["facebook", "Facebook"],
    ["website", "Website"],
    ["other", "Other / Link"],
];

// Athlete contact list — adds MaxPreps + Hudl links right after Facebook.
export const ATHLETE_CONTACT_TYPES = [
    ["phone", "Phone"],
    ["email", "Email"],
    ["instagram", "Instagram"],
    ["tiktok", "TikTok"],
    ["youtube", "YouTube"],
    ["x", "X (Twitter)"],
    ["facebook", "Facebook"],
    ["maxpreps", "MaxPreps"],
    ["hudl", "Hudl"],
    ["website", "Website"],
    ["other", "Other / Link"],
];

// Superset label lookup (covers every type across all contexts) for display components.
export const CONTACT_LABELS = Object.fromEntries(ATHLETE_CONTACT_TYPES);

const PLACEHOLDER = {
    email: "coach@example.com",
    phone: "(904) 555-0100",
    instagram: "@username or username url",
    tiktok: "@username or username url",
    youtube: "@username or username url",
    x: "@username or username url",
    facebook: "@username or username url",
    maxpreps: "maxpreps.com athlete bio url",
    hudl: "hudl.com profile url",
    website: "https://example.com/tryouts",
    other: "https://…",
};

export function ContactIcon({ type, className = "h-3.5 w-3.5" }) {
    if (type === "email") return <Mail className={className} />;
    if (type === "phone") return <Phone className={className} />;
    if (type === "instagram") return <Instagram className={className} />;
    if (type === "tiktok") return <Music2 className={className} />;
    if (type === "youtube") return <Youtube className={className} />;
    if (type === "x") return <XIcon className={className} />;
    if (type === "facebook") return <Facebook className={className} />;
    if (type === "maxpreps") return <Trophy className={className} />;
    if (type === "hudl") return <Video className={className} />;
    if (type === "website") return <Globe className={className} />;
    return <LinkIcon className={className} />;
}

// Normalize a contact value for identity comparison (matches backend claim_utils).
export function normContactValue(type, value) {
    const s = (value || "").trim().toLowerCase();
    if (!s) return "";
    if (type === "phone") return s.replace(/\D/g, "");
    return s.replace(/^@/, "").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");
}

const contactKey = (row) => `${(row.type || "").toLowerCase()}|${normContactValue(row.type, row.value)}`;

/**
 * Editable list of contact channels used on the Add Event form.
 * Value is an array of { type, value } objects; empty rows are allowed while
 * editing but filtered out on submit by the parent.
 *
 * lockedKeys: rows whose "type|normvalue" is in here are read-only (claimed
 * listing edited by a non-owner). checkKeys: rows shown with a green ✓ ("verifies
 * your claim" — the owner editing their own claimed listing).
 */
export default function ContactsField({ value, onChange, testidPrefix = "event-contact", accent = "#ff4500", lockedKeys = [], checkKeys = [], types = CONTACT_TYPES }) {
    const rows = useMemo(() => (value && value.length ? value : [{ type: "phone", value: "" }]), [value]);
    const lockedSet = useMemo(() => new Set(lockedKeys), [lockedKeys]);
    const checkSet = useMemo(() => new Set(checkKeys), [checkKeys]);

    const updateRow = (i, patch) => {
        const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
        onChange(next);
    };
    const lastRow = rows[rows.length - 1];
    const canAdd = !!((lastRow?.value || "").trim());
    const addRow = () => {
        if (!canAdd) return; // must fill the current line before opening another
        onChange([...rows, { type: "phone", value: "" }]);
    };
    const removeRow = (i) => {
        if (rows.length === 1) {
            onChange([{ type: "phone", value: "" }]);
        } else {
            onChange(rows.filter((_, idx) => idx !== i));
        }
    };

    return (
        <div data-testid={testidPrefix}>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] flex items-center justify-between">
                <span>Contact / Social Media <span className="text-[#ff4500] ml-0.5">*</span></span>
                <span className="text-[10px] normal-case tracking-normal text-[#a0a0a0]">At least one point of contact</span>
            </Label>
            <div className="mt-2 space-y-2">
                {rows.map((row, i) => {
                    const hasVal = !!(row.value || "").trim();
                    const locked = hasVal && lockedSet.has(contactKey(row));
                    const checked = hasVal && checkSet.has(contactKey(row));
                    return (
                    <div key={i} className="flex gap-2 items-stretch" data-testid={`${testidPrefix}-row-${i}`}>
                        <div className="w-[160px] shrink-0">
                            <Select value={row.type} onValueChange={(v) => { if (v !== row.type) updateRow(i, { type: v, value: "" }); }} disabled={locked}>
                                <SelectTrigger data-testid={`${testidPrefix}-type-${i}`} className="h-11 rounded-none bg-[#141414] border-[#2a2a2a] disabled:opacity-70">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                                    {types.map(([v, lbl]) => (
                                        <SelectItem key={v} value={v}>
                                            <div className="flex items-center gap-2">
                                                <ContactIcon type={v} />
                                                {lbl}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {row.type === "phone" ? (
                            <PhoneInput
                                data-testid={`${testidPrefix}-value-${i}`}
                                value={row.value}
                                onChange={(v) => updateRow(i, { value: v })}
                                disabled={locked}
                                className="h-11 rounded-none bg-[#141414] border-[#2a2a2a] flex-1 disabled:opacity-70"
                            />
                        ) : (
                            <Input
                                data-testid={`${testidPrefix}-value-${i}`}
                                value={row.value}
                                disabled={locked}
                                onChange={(e) => updateRow(i, { value: e.target.value })}
                                onBlur={(e) => {
                                    if (SOCIAL_TYPES.includes(row.type)) {
                                        const clean = extractSocialHandle(row.type, e.target.value);
                                        if (clean !== row.value) updateRow(i, { value: clean });
                                    }
                                }}
                                placeholder={PLACEHOLDER[row.type] || ""}
                                className="h-11 rounded-none bg-[#141414] border-[#2a2a2a] flex-1 disabled:opacity-70"
                            />
                        )}
                        {checked && (
                            <div data-testid={`${testidPrefix}-verifies-${i}`} title="Verifies your claim" className="h-11 px-2 shrink-0 flex items-center gap-1 text-[#22c55e] text-[10px] uppercase tracking-[0.12em]">
                                <Check className="h-4 w-4" /> Verifies
                            </div>
                        )}
                        {locked ? (
                            <div data-testid={`${testidPrefix}-locked-${i}`} title="Locked — set by the verified owner" className="h-11 w-11 shrink-0 border border-[#2a2a2a] text-[#8a8a8a] flex items-center justify-center">
                                <Lock className="h-4 w-4" />
                            </div>
                        ) : (
                            <button
                                type="button"
                                data-testid={`${testidPrefix}-remove-${i}`}
                                onClick={() => removeRow(i)}
                                className="h-11 w-11 shrink-0 border border-[#2a2a2a] text-[#666] hover:text-[#ff4500] hover:border-[#ff4500] flex items-center justify-center transition-colors"
                                aria-label="Remove contact"
                                title={rows.length === 1 ? "Clear this contact" : "Remove this contact"}
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    );
                })}
            </div>
            <button
                type="button"
                data-testid={`${testidPrefix}-add`}
                onClick={addRow}
                disabled={!canAdd}
                style={{ color: canAdd ? accent : "#555" }}
                title={canAdd ? "" : "Enter this contact's info before adding another"}
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] hover:underline disabled:no-underline disabled:cursor-not-allowed"
            >
                <Plus className="h-3 w-3" /> Save / Add another contact
            </button>
        </div>
    );
}
